import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Loader, RefreshCcw, Search, Upload, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import { useAuth } from '../../contexts/AuthProvider';

const AUDIT_MODULE = 'audit';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const MatchBadge = ({ status }) => {
  if (status === 'MATCHED') return <span className="badge bg-success">Matched</span>;
  if (status === 'CREATED') return <span className="badge bg-primary" title="New CRM user auto-created">Auto-created</span>;
  return <span className="badge bg-warning text-dark" title="Email not found in CRM">Unmatched</span>;
};

const CompetencyManagement = () => {
  const alertTimerRef = useRef(null);
  const { can, hasRole, capabilitiesLoading } = useAuth();
  const isAdmin = hasRole('ROLE_ADMIN');
  const isDirector = hasRole('ROLE_DIRECTOR');
  const canViewPage =
    isAdmin
    || isDirector
    || can('view_own', AUDIT_MODULE)
    || can('view_department', AUDIT_MODULE)
    || can('view_global', AUDIT_MODULE);

  /* ── list state ── */
  const [records, setRecords]       = useState([]);
  const [stats, setStats]           = useState({ total: 0, matched: 0, unmatched: 0 });
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pageSize, setPageSize]     = useState(25);
  const [page, setPage]             = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [alert, setAlert]           = useState(null);

  /* ── import modal state ── */
  const [modalStep, setModalStep]       = useState('closed');
  const [previewData, setPreviewData]   = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [previewFilter, setPreviewFilter] = useState('ALL');

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 5000);
  }, []);

  useEffect(() => () => {
    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
  }, []);

  /* ── fetch competencies ── */
  const fetchRecords = useCallback(async (pg = page, sz = pageSize, s = appliedSearch) => {
    setLoading(true);
    try {
      const params = { page: pg, size: sz };
      if (s) params.search = s;
      const res = await api.get('/audit/competencies/from-auditq', { params });
      const data = res.data;
      setRecords(Array.isArray(data.content) ? data.content : []);
      setTotalElements(data.totalElements ?? 0);
      setTotalPages(data.totalPages ?? 1);
      if (data.stats) setStats(data.stats);
    } catch (err) {
      showAlert('danger', err?.response?.data?.message || 'Failed to load competency data.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedSearch, showAlert]);

  useEffect(() => {
    if (capabilitiesLoading || !canViewPage) return;
    fetchRecords(page, pageSize, appliedSearch);
  }, [capabilitiesLoading, canViewPage, page, pageSize, appliedSearch, fetchRecords]);

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  /* ── export ── */
  const handleExport = async () => {
    try {
      const res = await api.get('/audit/competencies/from-auditq', { params: { page: 0, size: 9999 } });
      const all = res.data?.content ?? [];
      const rows = all.map((r, i) => ({
        No: i + 1,
        'Staff Name': r.staffName,
        Email: r.staffEmail,
        Qualification: r.qualificationName,
        Standard: r.standardCode,
        'Match Status': r.matchStatus,
        'CRM User': r.crmUsername ?? '—',
        'Imported At': r.importedAt ? new Date(r.importedAt).toLocaleString() : '—',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 6 }, { wch: 28 }, { wch: 32 }, { wch: 26 }, { wch: 26 }, { wch: 14 }, { wch: 28 }, { wch: 20 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Competency from AuditQ');
      XLSX.writeFile(wb, `competency_auditq_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      showAlert('danger', 'Export failed.');
    }
  };

  /* ── import flow ── */
  const handleOpenImport = async () => {
    setModalStep('previewing');
    setPreviewData(null);
    setImportResult(null);
    setPreviewFilter('ALL');
    try {
      const res = await api.get('/audit/staff-competency/preview-from-auditq');
      setPreviewData(res.data);
      setModalStep('preview');
    } catch (err) {
      setModalStep('closed');
      showAlert('danger', err?.response?.data?.message || 'Failed to load preview.');
    }
  };

  const handleApplyImport = async () => {
    setModalStep('importing');
    try {
      const res = await api.post('/audit/staff-competency/import-from-auditq');
      const importData = res.data;

      // Auto-sync to OneDataHub immediately after a successful import
      let syncSummary = null;
      if (importData.success && importData.imported > 0) {
        try {
          const syncRes = await api.post('/audit/staff-competency/sync-to-onedatahub');
          syncSummary = syncRes.data;
        } catch (syncErr) {
          // Non-fatal — log but don't fail the import result display
          console.error('Auto-sync to OneDataHub failed after import:', syncErr);
          syncSummary = { synced: 0, skipped: 0, errors: 1, errorMessage: syncErr?.response?.data?.message || syncErr.message };
        }
      }

      setImportResult({ ...importData, syncSummary });
      setModalStep('done');

      if (importData.imported > 0) {
        setPage(0);
        setAppliedSearch('');
        setSearch('');
        fetchRecords(0, pageSize, '');
      }
    } catch (err) {
      setImportResult({ success: false, message: err?.response?.data?.message || 'Import failed.' });
      setModalStep('done');
    }
  };

  const handleCloseModal = () => {
    if (modalStep === 'importing') return;
    setModalStep('closed');
    setPreviewData(null);
    setImportResult(null);
  };

  /* ── AuditQ datasource status ── */
  const [auditqConfigured, setAuditqConfigured] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/audit/staff-competency/auditq-status')
      .then(res => setAuditqConfigured(res.data?.configured === true))
      .catch(() => setAuditqConfigured(false));
  }, [isAdmin]);

  /* ── sync to OneDataHub ── */
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSyncToOnedatahub = async () => {
    setIsSyncing(true);
    try {
      const res = await api.post('/audit/staff-competency/sync-to-onedatahub');
      const d = res.data;
      showAlert('success', `Sync complete: ${d.synced} synced, ${d.skipped} skipped${d.errors > 0 ? `, ${d.errors} errors` : ''}.`);
    } catch (err) {
      showAlert('danger', err?.response?.data?.message || 'Sync to OneDataHub failed.');
    } finally {
      setIsSyncing(false);
    }
  };

  const isModalOpen  = modalStep !== 'closed';
  const isProcessing = modalStep === 'previewing' || modalStep === 'importing';

  const filteredPreview = useMemo(() => {
    if (!previewData?.preview) return [];
    if (previewFilter === 'ALL') return previewData.preview;
    return previewData.preview.filter((i) => i.action === previewFilter);
  }, [previewData, previewFilter]);

  const pageItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const pages = [0];
    const start = Math.max(1, page - 1);
    const end   = Math.min(totalPages - 2, page + 1);
    if (start > 1) pages.push('...');
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < totalPages - 2) pages.push('...');
    pages.push(totalPages - 1);
    return pages;
  }, [page, totalPages]);

  /* ────────────────────────────────────────── */
  if (capabilitiesLoading) {
    return (
      <div className="container-xl py-5 text-center text-muted">
        <Loader className="icon icon-spin mx-auto mb-2 d-block" size={24} />
        Loading competency data...
      </div>
    );
  }

  if (!canViewPage) return <Navigate to="/dashboard" replace />;

  return (
    <div className="container-xl">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* ── Import Modal ── */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055 }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Upload size={18} className="text-primary" />
                  Import Competency Mapping from AuditQ
                </h5>
                {!isProcessing && (
                  <button type="button" className="btn-close" onClick={handleCloseModal} />
                )}
              </div>

              <div className="modal-body">
                {/* Previewing */}
                {modalStep === 'previewing' && (
                  <div className="py-5 text-center text-muted">
                    <Loader className="icon icon-spin mx-auto mb-2 d-block" size={28} />
                    <div className="fw-semibold">Fetching data from AuditQ production...</div>
                    <div className="small mt-1 text-muted">Checking 783 technical qualification records</div>
                  </div>
                )}

                {/* Preview table */}
                {(modalStep === 'preview' || modalStep === 'importing') && previewData && (
                  <>
                    <div className="row g-3 mb-3">
                      {[
                        { label: 'Total AuditQ records', value: previewData.total, bg: 'bg-light', cls: 'text-dark' },
                        { label: 'Will be imported', value: previewData.willImport, bg: '', style: { background: '#d1fae5' }, cls: 'text-success' },
                        { label: 'Already imported (skip)', value: previewData.willSkip, bg: 'bg-light', cls: 'text-muted' },
                      ].map((c) => (
                        <div key={c.label} className="col-4">
                          <div className={`card text-center border-0 ${c.bg}`} style={c.style}>
                            <div className="card-body py-3">
                              <div className={`h3 mb-0 fw-bold ${c.cls}`}>{c.value}</div>
                              <div className="small text-muted">{c.label}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="alert alert-info py-2 small mb-3">
                      <strong>Zero data loss — all {previewData.willImport} records will be stored:</strong>
                      <ul className="mb-0 mt-1">
                        <li>Staff not in CRM → auto-registered with password <code>MgrtAuditQ</code></li>
                        <li>Qualification not in CRM → auto-added to PersonnelQualification master</li>
                        <li>All records linked to CRM entities after import</li>
                      </ul>
                    </div>

                    <div className="d-flex gap-2 mb-3">
                      {['ALL', 'IMPORT', 'SKIP'].map((f) => (
                        <button
                          key={f}
                          type="button"
                          className={`btn btn-sm ${previewFilter === f ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setPreviewFilter(f)}
                          disabled={modalStep === 'importing'}
                        >
                          {f === 'ALL' && `All (${previewData.total})`}
                          {f === 'IMPORT' && `To Import (${previewData.willImport})`}
                          {f === 'SKIP' && `Already exists (${previewData.willSkip})`}
                        </button>
                      ))}
                    </div>

                    <div className="table-responsive" style={{ maxHeight: 340 }}>
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Staff Name</th>
                            <th>Email</th>
                            <th>Qualification</th>
                            <th>Standard</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPreview.length === 0 ? (
                            <tr><td colSpan="5" className="text-center text-muted py-4">No records</td></tr>
                          ) : filteredPreview.map((item, idx) => (
                            <tr key={idx} className={item.action !== 'IMPORT' ? 'text-muted' : ''}>
                              <td className="fw-semibold small">{item.staffName}</td>
                              <td className="small">{item.email}</td>
                              <td className="small">{item.qualification}</td>
                              <td className="small">{item.standard}</td>
                              <td>
                                {item.action === 'IMPORT' ? (
                                  <span className="badge bg-success">Import</span>
                                ) : (
                                  <span className="badge bg-secondary">Skip</span>
                                )}
                                {item.note && (
                                  <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                    — {item.note}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {modalStep === 'importing' && (
                      <div className="alert alert-info mt-3 mb-0 d-flex align-items-center gap-2">
                        <Loader className="icon icon-spin flex-shrink-0" size={16} />
                        Importing {previewData.willImport} records... Do not close this window.
                      </div>
                    )}
                  </>
                )}

                {/* Done */}
                {modalStep === 'done' && importResult && (
                  <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'} mb-0`}>
                    {importResult.success ? (
                      <>
                        <strong>Import complete.</strong>{' '}
                        <span className="badge bg-success me-1">{importResult.imported} imported</span>
                        {importResult.created > 0 && (
                          <span className="badge bg-primary me-1">{importResult.created} new CRM users created</span>
                        )}
                        <span className="badge bg-secondary me-1">{importResult.skipped} skipped</span>
                        {importResult.errors > 0 && (
                          <span className="badge bg-warning text-dark">{importResult.errors} errors</span>
                        )}
                        {importResult.errorList?.length > 0 && (
                          <details className="mt-2">
                            <summary className="small" style={{ cursor: 'pointer' }}>
                              Show errors ({importResult.errorList.length})
                            </summary>
                            <ul className="small mt-1 mb-0">
                              {importResult.errorList.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          </details>
                        )}
                      </>
                    ) : (
                      <><strong>Import failed.</strong> {importResult.message}</>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                {modalStep === 'done' ? (
                  <button type="button" className="btn btn-primary" onClick={handleCloseModal}>Close</button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCloseModal}
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleApplyImport}
                      disabled={isProcessing || modalStep !== 'preview' || (previewData?.willImport ?? 0) === 0}
                    >
                      {modalStep === 'importing'
                        ? <><Loader className="icon icon-spin me-1" size={15} />Importing...</>
                        : <><Upload size={15} className="me-1" />Apply Import ({previewData?.willImport ?? 0})</>}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title mb-1">Competency Mapping — AuditQ</h2>
          <div className="text-muted small">
            Staff competency data imported from AuditQ production.
            {stats.total > 0 && (
              <> &nbsp;
                <span className="badge bg-success-lt text-success me-1">{stats.matched} matched</span>
                {stats.created > 0 && <span className="badge bg-blue-lt text-blue me-1">{stats.created} auto-created</span>}
                {stats.unmatched > 0 && <span className="badge bg-warning-lt text-warning">{stats.unmatched} unmatched</span>}
              </>
            )}
          </div>
        </div>
        <div className="col-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => fetchRecords(page, pageSize, appliedSearch)}
            disabled={loading || isProcessing}
          >
            {loading ? <Loader className="icon icon-spin" size={16} /> : <RefreshCcw size={16} className="me-1" />}
            Refresh
          </button>
          {stats.total > 0 && (
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handleExport}
              disabled={loading}
            >
              <Download size={16} className="me-1" />
              Export Excel
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handleSyncToOnedatahub}
              disabled={isSyncing || isProcessing}
              title="Push imported competencies to OneDataHub (md.nexoratech.co)"
            >
              {isSyncing
                ? <><Loader size={14} className="me-1 spin-animation" />Syncing…</>
                : <><RefreshCcw size={14} className="me-1" />Sync to OneDataHub</>}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleOpenImport}
              disabled={isProcessing || loading}
              title={auditqConfigured === false
                ? 'AuditQ datasource not configured. Add AUDITQ_DB_URL, AUDITQ_DB_USER, AUDITQ_DB_PASS to CLOUD_PROPERTIES secret.'
                : 'Import competency mapping from AuditQ production database'}
            >
              <Upload size={16} className="me-1" />
              Import from AuditQ
            </button>
          )}
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="input-group" style={{ maxWidth: 420 }}>
            <span className="input-group-text bg-white"><Search size={15} className="text-muted" /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search name, email, qualification, or standard..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <button type="button" className="btn btn-primary" onClick={handleSearch} disabled={loading}>
              Search
            </button>
            {appliedSearch && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => { setSearch(''); setPage(0); setAppliedSearch(''); }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold">
            <Users size={16} className="me-1 text-muted" />
            AuditQ Competency Records
          </span>
          <span className="badge bg-secondary">{totalElements}</span>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 44 }}>No</th>
                <th>Staff Name</th>
                <th>Email</th>
                <th>Qualification</th>
                <th>Standard</th>
                <th>User</th>
                <th>Qual.</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <Loader className="icon icon-spin mx-auto mb-2 d-block" />
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    {totalElements === 0
                      ? 'No competency data imported yet. Click "Import from AuditQ" to get started.'
                      : 'No records match your search.'}
                  </td>
                </tr>
              ) : records.map((r, idx) => (
                <tr key={r.id ?? idx}>
                  <td className="text-muted small">{page * pageSize + idx + 1}</td>
                  <td className="fw-semibold small">{r.staffName}</td>
                  <td className="small text-muted">{r.staffEmail}</td>
                  <td className="small">{r.qualificationName}</td>
                  <td><span className="badge bg-azure-lt text-azure small">{r.standardCode}</span></td>
                  <td><MatchBadge status={r.matchStatus} /></td>
                  <td>
                    {r.qualificationMapped
                      ? <span className="badge bg-success">✓</span>
                      : <span className="badge bg-secondary">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="text-muted small">
            {totalElements === 0 ? '0 records' : (
              <>
                {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
              </>
            )}
          </div>
          <div className="d-flex align-items-center gap-3 ms-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 88 }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                  <button type="button" className="page-link" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
                    Previous
                  </button>
                </li>
                {pageItems.map((p, i) => (
                  typeof p === 'number' ? (
                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                      <button type="button" className="page-link" onClick={() => setPage(p)}>{p + 1}</button>
                    </li>
                  ) : (
                    <li key={`e${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                  )
                ))}
                <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                  <button type="button" className="page-link" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetencyManagement;
